"use client";

import React, { createContext, useContext, useEffect, useState } from "react";
import { io, Socket } from "socket.io-client";
import { useAccount } from "./AccountContext"; // Safely positioned above in hierarchy

const SocketContext = createContext<Socket | null>(null);

export const useSocket = () => useContext(SocketContext);

export const SocketProvider = ({ children }: { children: React.ReactNode }) => {
  const [socket, setSocket] = useState<Socket | null>(null);
  
  // 1. Correctly place the hook INSIDE the functional component
  const { selectedAccount } = useAccount();

  useEffect(() => {
    // Initialize the socket instance once when the provider mounts
    const socketInstance = io("https://app.atajoy.com", {
      path: "/sapi/socket.io",
      withCredentials: true,
      transports: ["polling", "websocket"],
      upgrade: true,
    });

    socketInstance.on("connect", () => {
      console.log("🔌 Global Provider Connected:", socketInstance.id);
    });

    setSocket(socketInstance);

    // Completely tear down instance on layout unmount
    return () => {
      socketInstance.disconnect();
    };
  }, []);

  // 2. Handle room switching dynamically when selectedAccount.id changes
  useEffect(() => {
    if (!socket || !selectedAccount?.id) return;

    const accountId = selectedAccount.igUserId;

    // Check if socket is connected before emitting, or wait for connect event
    const joinRoom = () => {
      // console.log(`🎯 Emitting join_account_room for ID: ${accountId}`);
      socket.emit("join_account_room", accountId);
    };

    if (socket.connected) {
      joinRoom();
    } else {
      socket.on("connect", joinRoom);
    }

    // CLEANUP METHOD: Runs right before switching to a new account ID
    return () => {
      console.log(`🚪 Leaving account room channel ID: ${accountId}`);
      socket.emit("leave_account_room", accountId);
      socket.off("connect", joinRoom);
    };
  }, [socket, selectedAccount?.id]); // Re-runs every single time an agent switches accounts

  return (
    <SocketContext.Provider value={socket}>
      {children}
    </SocketContext.Provider>
  );
};